/**
 * Short, readable novel links: `/novel/<title-slug>-<code>` (and `/n/<code>`).
 *
 * `code` is the novel's permanent 6-character `short_code`, minted by the
 * database (migration 0039) from an alphabet without look-alikes (no 0/o,
 * 1/l/i). Only the code identifies the novel — the slug is decoration, so a
 * renamed novel keeps working links and a wrong slug still resolves. Legacy ids
 * (`novel_anthropic_…`, which contain underscores) keep working too.
 */

export const SHORT_CODE_ALPHABET = "23456789abcdefghjkmnpqrstuvwxyz";
const CODE_PATTERN = `[${SHORT_CODE_ALPHABET}]{6}`;
const CODE_ONLY = new RegExp(`^${CODE_PATTERN}$`);
const KEY_TAIL = new RegExp(`^[a-z0-9-]*-(${CODE_PATTERN})$`);

const CYRILLIC: Record<string, string> = {
  а: "a", б: "b", в: "v", г: "g", д: "d", е: "e", ё: "e", ж: "zh", з: "z", и: "i", й: "y",
  к: "k", л: "l", м: "m", н: "n", о: "o", п: "p", р: "r", с: "s", т: "t", у: "u", ф: "f",
  х: "kh", ц: "ts", ч: "ch", ш: "sh", щ: "shch", ъ: "", ы: "y", ь: "", э: "e", ю: "yu", я: "ya",
  // Kazakh / Ukrainian letters that appear in titles.
  ә: "a", ғ: "g", қ: "q", ң: "n", ө: "o", ұ: "u", ү: "u", һ: "h", і: "i", ї: "yi", є: "ye", ґ: "g"
};

const MAX_SLUG_LENGTH = 60;

/** A URL slug for a title: lowercase Latin, digits and hyphens; "" when nothing readable is left. */
export function novelSlug(title: string): string {
  const latin = title
    .toLowerCase()
    .replace(/[Ѐ-ӿ]/g, (ch) => CYRILLIC[ch] ?? "")
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  if (latin.length <= MAX_SLUG_LENGTH) return latin;
  const cut = latin.slice(0, MAX_SLUG_LENGTH);
  const lastBreak = cut.lastIndexOf("-");
  return (lastBreak > 20 ? cut.slice(0, lastBreak) : cut).replace(/-+$/, "");
}

/** The path segment for a novel: "<slug>-<code>", or just "<code>" for a title with no Latin rendering. */
export function novelLinkKey(title: string, shortCode: string): string {
  const slug = novelSlug(title);
  return slug ? `${slug}-${shortCode}` : shortCode;
}

export function isShortCode(value: string): boolean {
  return CODE_ONLY.test(value);
}

/**
 * The short code a link segment carries ("<slug>-<code>" or "<code>"), or null
 * for anything else — including every legacy id, which contains "_".
 */
export function shortCodeFromLinkKey(segment: string): string | null {
  const value = segment.trim().toLowerCase();
  if (value.includes("_")) return null;
  if (CODE_ONLY.test(value)) return value;
  return KEY_TAIL.exec(value)?.[1] ?? null;
}
